import { useState, useEffect, memo } from 'react';
import { Link } from 'react-router-dom';
import { FaFacebookF, FaTwitter, FaInstagram, FaYoutube, FaWhatsapp } from '../utility/react-icons';
import { createSlug } from '../utility/HelperFunction';
import { getData } from "../apiService";
import { useNavigate } from "react-router-dom";
import Newsletter from './Notification/Newsletter';

const IMAGE_BASE_URL = import.meta.env.VITE_BOOK_URL;

const SOCIALS = [
  { href: 'https://www.facebook.com/karkathamizha', Icon: FaFacebookF, label: 'Facebook' },
  { href: 'https://x.com/karkathamizha', Icon: FaTwitter, label: 'Twitter / X' },
  { href: 'https://www.instagram.com/karkathamizha', Icon: FaInstagram, label: 'Instagram' },
  { href: 'https://www.youtube.com/@karkathamizha', Icon: FaYoutube, label: 'YouTube' },
  { href: 'https://wa.me/918189989911', Icon: FaWhatsapp, label: 'WhatsApp' },
];

const AWARDS = [
  'சிறந்த நாவல் – 2024',
  'சிறந்த சிறுகதைத் தொகுப்பு – 2024',
  'சிறந்த கவிதைத் தொகுப்பு – 2024',
  'சிறந்த கட்டுரைத் தொகுப்பு – 2024',
  'சிறந்த மொழிபெயர்ப்பு (சிறுகதை) – 2024',
  'சிறந்த மொழிபெயர்ப்பு (கவிதை) – 2024',
  'சிறந்த மொழிபெயர்ப்பு (கட்டுரை) – 2024',
];

/* ── Skeleton ───────────────────────────────────────────────────── */
const ReviewSkeleton = () => (
  <div style={{ minHeight: '296px' }}>
    {[1, 2, 3].map(i => (
      <div key={i} className="ft-review">
        <div style={{
          width: 56, minWidth: 56, height: 74, minHeight: 74,
          background: 'rgba(255,255,255,0.1)', borderRadius: 3, flexShrink: 0
        }} />
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 6 }}>
          <div style={{ height: 10, width: '40%', background: 'rgba(255,255,255,0.1)', borderRadius: 3 }} />
          <div style={{ height: 12, width: '90%', background: 'rgba(255,255,255,0.1)', borderRadius: 3 }} />
          <div style={{ height: 12, width: '60%', background: 'rgba(255,255,255,0.1)', borderRadius: 3 }} />
        </div>
      </div>
    ))}
  </div>
);

/* ── Single review row ──────────────────────────────────────────── */
const FooterReviewItem = ({ review }) => (
  <article className="ft-review">
    <Link
      to={`/bookreviews/${review.bookId}/${createSlug(review.bookName)}`}
      aria-label={review.bookName}
      tabIndex={-1}
    >
      <img
        src={`${IMAGE_BASE_URL}${review.imgBookSmallFS}`}
        alt={review.bookName}
        className="ft-review-img"
        width="56"
        height="74"
        loading="lazy"
        decoding="async"
      />
    </Link>
    <div className="ft-review-body">
      <time className="ft-review-date" dateTime={review.reviewDate}>
        {new Date(review.reviewDate).toLocaleDateString('ta-IN', {
          day: '2-digit', month: '2-digit', year: 'numeric',
        })}
      </time>
      <Link
        to={`/bookreviews/${review.bookId}/${createSlug(review.bookName)}`}
        className="ft-review-title"
        style={{
          minHeight: '35px', display: 'flex', flexDirection: 'column',
          justifyContent: 'center', padding: '2px 0'
        }}
      >
        <h3>{review.bookName}</h3>
      </Link>
      <Link
        to={`/user/${review.userId}/${createSlug(review.userName)}`}
        className="ft-review-author"
        style={{ minHeight: '30px', display: 'flex', alignItems: 'center' }}
      >
        — {review.userName}
      </Link>
    </div>
  </article>
);

/* ── Footer ─────────────────────────────────────────────────────── */
const FooterComponent = () => {
  const [latestReviews, setLatestReviews] = useState([]);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    fetchReviews();
  }, []);


  const fetchReviews = async () => {
    try {
      setLoading(true);
      const response = await getData('Home/GetLatestReviews', null, navigate);
      setLatestReviews(response.latestReviews || []);
    } catch (error) {
      setLatestReviews([]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <footer className="ft-root">
      <div className="container-fluid">

        {/* ✅ Fix: use g-0 to remove gutter issues, ensure row renders all 3 cols */}
        <div className="row g-3">

          {/* Col 1 — Contact + Social */}
          <div className="col-12 col-md-4">
            <div className="ft-col">
              <h2 className="ft-heading">தொடர்பு கொள்ளுங்கள்</h2>
              <address className="ft-contact">
                <p>வாட்ஸ் அப்: <a href="tel:+918189989911">81 899 899 11</a></p>
                <p>பேச: <a href="tel:+918122885555">81 22 88 5555</a></p>
                <p>மின்னஞ்சல்: <a href="mailto:karkathamizha@gmail.com">karkathamizha@gmail.com</a></p>
              </address>

              <h2 className="ft-heading mt-3">சமூக வலைதளங்கள்</h2>
              <nav className="ft-socials" aria-label="சமூக வலைதளங்கள்">
                {SOCIALS.map(({ href, Icon, label }) => (
                  <a key={label} href={href} className="ft-social-btn"
                    aria-label={label} target="_blank" rel="noopener noreferrer">
                    <Icon aria-hidden="true" />
                  </a>
                ))}
              </nav>
            </div>
          </div>

          {/* Col 2 — Awards + Newsletter */}
          <div className="col-12 col-md-4">
            <div className="ft-col">
              <h2 className="ft-heading">விருதுகள் – 2025</h2>
              <ul className="ft-awards" aria-label="2025 விருதுகள்">
                {AWARDS.map(award => (
                  <li key={award}>{award}</li>
                ))}
              </ul>
              <div className="mt-3">
                <Newsletter />
              </div>
            </div>
          </div>

          {/* Col 3 — Recent Reviews */}
          <div className="col-12 col-md-4">
            <div className="ft-col">
              <h2 className="ft-heading">சமீபத்திய விமர்சனங்கள்</h2>

              {loading ? (
                <ReviewSkeleton />
              ) : latestReviews.length === 0 ? (
                <div style={{ minHeight: '296px' }}>
                  <p className="ft-review-date">விமர்சனங்கள் இல்லை.</p>
                </div>
              ) : (
                <div style={{ minHeight: '296px' }}>
                  {latestReviews.map(review => (
                    <FooterReviewItem key={review.id} review={review} />
                  ))}
                </div>
              )}
            </div>
          </div>

        </div>

        {/* Copyright */}
        <p className="ft-copy">
          © {new Date().getFullYear()} கற்க தமிழா. அனைத்து உரிமைகளும் பாதுகாக்கப்பட்டவை.
        </p>
      </div>
    </footer>
  )
};

export const Footer = memo(FooterComponent);